import { appSync } from '../app/app_sync.js';
import { error, log } from '../app/logger.js';
import { navigation } from '../app/navigation.js';
import { mapHandler } from '../map/map_handler.js';
import { PanelBase } from '../ui/panel_base.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { animateUILoading } from '../ui/ui_loader.js';

import './loc_view_full.js';

/**
 * Location panel, the container for location page.
 */
class LocPanel extends PanelBase {
  static id = navigation.kLocPanelId;

  static ownsPage(page) {
    return /loc(\/\d+)?$/.test(page);
  }

  static get selectors() {
    return Object.assign({}, super.selectors, {
      LocView: 'loc-view-full',
    });
  }

  static get elements() {
    return {
      locView: this.selectors.LocView,
    };
  }

  static markup = `<loc-view-full></loc-view-full>`;

  async switchOn(params) {
    let locId = params?.locId ?? this.#loc?.id;
    if (!locId) {
      error(
        `[locpage] no location is given to open the locpage, showid: ${
          this.#showid
        }`
      );
      return null;
    }

    const showid = (this.#showid = Math.random());
    this.params = params;

    // Grab location data from markup.
    log(`[locpage] open for ${locId}, showid: ${this.#showid}`);

    super.switchOn();

    const map = await mapHandler.attachMapIfNot(this.locView.mapHolderElement);

    // The panel was switched off during map initialization
    // or was reshown.
    if (!this.active || showid != this.#showid) {
      return;
    }

    this.locView.setup(map);

    // Update UI if needed.
    if (locId != this.#loc?.id) {
      await animateUILoading(
        this.locView,
        map.locs.load(locId).then(loc => this.locView.set(loc))
      );
    }

    if (this.#loc.id) {
      appSync.syncLoc.connect(this.#loc.id);
    }

    log(`[locpage] map loaded, initialize`);
    if (showid != this.#showid) {
      log(`[locpage] reopen, old showid: ${showid}, showid: ${this.#showid}`);
      return null;
    }

    const { north, south, east, west } = await this.#loc.getArea();
    if (north != south && east != west) {
      log(
        `[locpage] fit map to area {north: ${north}, south: ${south}, east: ${east}, west: ${west}}`
      );
      map.fitBounds({ north, south, east, west });
    } else {
      log(
        `[locpage] fit map to location {lat: ${this.#loc.lat}, lng: ${
          this.#loc.lng
        }}`
      );
      map.panAndZoomTo(this.#loc);
    }

    // Wait for the locations loaded. If no the page location is available, then
    // wait for the next notification. This happens, for example, when initial
    // event and panTo caused event are not merged.
    let counter = 0;
    let marker = null;
    do {
      await map.waitfor('locations_loaded');
      if (showid != this.#showid) {
        log(`[locpage] reopen, old showid: ${showid}, showid: ${this.#showid}`);
        return;
      }
      marker = this.#loc.marker;
      log(
        `[locpage] locations_loaded.${++counter}, ${this.#loc.name}:${
          this.#loc.id
        } location has marker: ${!!marker}`
      );
    } while (!marker);

    if (this.locView.swapMarker) {
      this.locView.swapMarker.swapFor(marker, this.#loc);
    }
    log(`[locpage] initialization complete, showid: ${showid}`);
  }

  switchOff() {
    log(`[locpage] LocPanel.close ${this.params.locId}`);
    this.locView.release();
    this.#showid = 0;
    super.switchOff();
  }

  getLocPage(loc) {
    return `loc/${loc.id}`;
  }

  getMap() {
    return mapHandler.getMap();
  }

  get historyState() {
    return [
      {
        page: (this.#loc && this.getLocPage(this.#loc)) || this.page,
        params: this.params,
      },
      this.#loc.title,
      `${window.location.origin}/loc?${this.#loc.id}&${this.#loc.name.replace(
        /\s/g,
        '-'
      )}`,
    ];
  }

  initialize() {
    super.initialize();
    listenToEvent(this.locView, 'fullmapswitch', ev =>
      this.#switchToFullMapFor(ev.detail)
    );
  }

  async #switchToFullMapFor(loc) {
    log(`[locpage] show fullmap for ${loc.id}:${loc.name}`);
    navigation.exploreMap(loc.id, this.page);
  }

  get #loc() {
    return this.locView.loc;
  }

  #showid;
}

navigation.registerPanel(LocPanel);

export { LocPanel };
